import { createStyles, Paper, Typography } from '@material-ui/core';
import { debounce } from 'lodash-es';
import React from 'react';

import { updatePasswordWithToken } from '~/src/dash/api/console-api';
import { IObject } from '~/src/dash/api/resource-transforms';
import { AsyncButton } from '~/src/dash/components/button';
import { FormItemTypePassword, IFormData, IFormItem, RenderForm } from '~/src/dash/components/form';
import { isLoginDisabled } from '~/src/dash/globals/disable-login';
import { getColorTheme, getRoundLogoURL } from '~/src/dash/resellers/utils/importResellerConfig';
import { IHistoryProps, IReduxActionProps, IWithStylesProps } from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { hasErrors, IErrors, transformErrors, validatorFactory } from '~/src/dash/utils/validator';
import { BaseClass } from '~/src/dash/views/base-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps extends IWithStylesProps, IReduxActionProps, IHistoryProps {}

interface IState {
  data: IFormData;
  email: string;
  token: string;
  showLoadingWheel: boolean;
  count: number;

  errors: IErrors;
  snackbarMessage: string;
  snackbarError: boolean;
}

const passwordResetForm: IFormItem[] = [
  {
    id: 'NewPassword',
    label: 'New Password',
    required: true,
    render: FormItemTypePassword,
    props: {
      disabled: isLoginDisabled,
    },
  },
  {
    id: 'ConfirmPassword',
    label: 'Confirm Password',
    required: true,
    render: FormItemTypePassword,
    props: {
      disabled: isLoginDisabled,
    },
  },
];

const validate = validatorFactory({
  NewPassword(val: any): string {
    if (!!val && val.length < 8) {
      return 'Your password needs to be at least 8 characters long.';
    }
    return '';
  },
  ConfirmPassword(val: any, _key: string, obj: IObject): string {
    if (!!val && val.length < 8) {
      return 'Your password needs to be at least 8 characters long.';
    } else if (val !== obj.NewPassword) {
      return 'Your passwords did not match';
    }
    return '';
  },
});

function extraParams(): string[] {
  const params = window.location.href.split('?')[1];
  const parsedParams = {} as { [key: string]: string };

  params.split('&').forEach((p: string) => {
    const t = p.split('=');
    parsedParams[t[0]] = t[1];
  });

  return [parsedParams.email || '', parsedParams.token || ''];
}

export const PasswordResetView = decorate(
  {
    styles: (theme: any) =>
      createStyles({
        paper: {
          ...theme.mixins.gutters(),
          paddingTop: theme.spacing.unit * 2,
          paddingBottom: theme.spacing.unit * 2,
          marginTop: theme.spacing.unit * 4,
          marginRight: 'auto',
          marginLeft: 'auto',
          maxWidth: '300px',
          [theme.breakpoints.up('md')]: {
            maxWidth: '400px',
            marginTop: theme.spacing.unit * 6,
          },
        },
        signHeader: {
          paddingTop: theme.spacing.unit * 4,
          textAlign: 'center',
          [theme.breakpoints.up('md')]: {
            paddingTop: theme.spacing.unit * 8,
          },
        },
        signHeaderLogo: {
          display: 'inline-block',
          width: '150px',
          height: '65px',
          background: 'url(' + getRoundLogoURL() + ') no-repeat center',
          backgroundSize: 'contain',
        },
        signHeaderText: {
          color: theme.palette.secondary.contrastText,
          fontSize: theme.spacing.unit * 4,
          margin: theme.spacing.unit * 3,
        },
      }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      data: {} as IFormData,
      email: extraParams()[0],
      token: extraParams()[1],
      showLoadingWheel: false,
      count: 0,
      errors: {} as IErrors,
      snackbarMessage: '',
      snackbarError: false,
    };

    _updatePassword = debounce((count: number) => {
      if (count !== this.state.count) {
        return;
      }
      updatePasswordWithToken({
        query: {
          email: this.state.email,
          token: this.state.token,
          password: this.state.data.NewPassword,
        },
      }).then(
        () => {
          if (count !== this.state.count) {
            return;
          }
          this.setState(
            {
              snackbarMessage:
                'Successfully updated your password. You will be redirected to sign in page in 3 secodns.',
              snackbarError: false,
              showLoadingWheel: false,
            },
            () => {
              setTimeout(() => {
                window.location.href = '/#/signin';
              }, 3000);
            },
          );
        },
        () => {
          if (count !== this.state.count) {
            return;
          }
          this.setState({
            snackbarMessage: 'Resetting password failed. Please try again later.',
            snackbarError: true,
            showLoadingWheel: false,
          });
        },
      );
    }, 200);

    constructor(props: IProps) {
      super(props);
    }

    updatePassword = () => {
      if (isLoginDisabled) {
        return;
      }

      if (!this.validateForm()) {
        this.setState({
          snackbarMessage: 'Resetting password failed. Please fix the errors and try again later.',
          snackbarError: true,
        });
        return;
      }
      const count = this.state.count + 1;
      this.setState({
        showLoadingWheel: true,
        count,
      });
      this._updatePassword(count);
    };

    onUpdate = (data: IFormData) => {
      if (hasErrors(this.state.errors)) {
        this.validateForm();
      }
      this.setState({ data });
    };

    handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
      evt.preventDefault();
      this.updatePassword();
    };

    validateForm = (): boolean => {
      const errors = validate(this.state.data);
      if (hasErrors(errors)) {
        this.setState({
          errors,
        });
        return false;
      }
      this.setState({
        errors: {} as IErrors,
      });
      return true;
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    render = () => {
      const { classes } = this.props;
      return (
        <div
          style={{
            width: '100%',
            height: '100vh',
            backgroundColor: getColorTheme('signInBackground'),
            margin: '0px 0px 0px 0px',
          }}
        >
          <BaseClass id='signin'>
            <div className={classes.signHeader}>
              <div className={classes.signHeaderLogo} />
              <h3 className={classes.signHeaderText}>Reset Password</h3>
            </div>
            <Paper
              classes={{
                root: classes.paper,
              }}
            >
              <form id='signin-form-body' onSubmit={this.handleSubmit}>
                <RenderForm
                  schema={passwordResetForm}
                  data={this.state.data}
                  errors={transformErrors(this.state.errors || {})}
                  onUpdate={this.onUpdate}
                />
                <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                  <div>
                    {isLoginDisabled && (
                      <Typography
                        component='p'
                        className='error-message'
                        style={{ marginBottom: '1rem' }}
                      >
                        Password reset is temporarily disabled.
                      </Typography>
                    )}
                  </div>

                  <AsyncButton
                    text='Update Password'
                    inprogress={this.state.showLoadingWheel}
                    props={{
                      size: 'large',
                    }}
                    onClick={this.updatePassword}
                    disabled={isLoginDisabled}
                  />
                </div>
              </form>
            </Paper>
            <SnackbarView
              open={!!this.state.snackbarMessage}
              error={!!this.state.snackbarError}
              onClose={this.snackbarClose}
            >
              {this.state.snackbarMessage}
            </SnackbarView>
          </BaseClass>
        </div>
      );
    };
  },
);
